"use client";

import { useTheme } from "next-themes";
import { Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

const themes = [
  { id: "light", label: "Light", icon: Sun },
  { id: "dark", label: "Dark", icon: Moon },
] as const;

export function ThemeSwitcher({
  className,
  onDark = true,
}: {
  className?: string;
  /** Match surrounding surface: dark header vs light drawer */
  onDark?: boolean;
}) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted) return;
    if (theme === "lumen") setTheme("dark");
  }, [mounted, theme, setTheme]);

  if (!mounted) {
    return (
      <div
        className={cn(
          "h-9 w-[4.75rem] rounded-full border sm:h-10 sm:w-[5.25rem]",
          onDark
            ? "border-white/20 bg-white/10"
            : "border-[rgba(10,28,58,0.14)] bg-white",
          className,
        )}
      />
    );
  }

  const active = theme === "light" ? "light" : "dark";

  return (
    <div
      className={cn(
        "relative inline-flex h-9 items-center rounded-full border p-0.5 backdrop-blur sm:h-10",
        onDark
          ? "border-white/20 bg-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
          : "border-[rgba(10,28,58,0.14)] bg-white shadow-[0_8px_24px_rgba(12,32,68,0.08)]",
        className,
      )}
      role="group"
      aria-label="Color theme"
      data-on-dark={onDark ? "true" : "false"}
    >
      {themes.map(({ id, label, icon: Icon }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            type="button"
            aria-label={`${label} theme`}
            aria-pressed={isActive}
            title={label}
            className={cn(
              "inline-flex h-full w-8 items-center justify-center rounded-full transition sm:w-9",
              isActive
                ? onDark
                  ? "bg-white text-[#0b1630] shadow-[0_8px_20px_rgba(0,0,0,0.25)]"
                  : "bg-[#0b1630] text-white shadow-[0_8px_20px_rgba(11,40,90,0.2)]"
                : onDark
                  ? "text-white/65 hover:text-white"
                  : "text-[#0b1630]/55 hover:text-[#0b1630]",
            )}
            onClick={() => setTheme(id)}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
          </button>
        );
      })}
    </div>
  );
}
